import type { App } from "@open-booking/server";
import type { CreateTenantPayload, UpdateTenantPayload } from "@open-booking/shared";
import { createApi } from "@reduxjs/toolkit/query/react";
import { type EdenCall, edenBaseQuery } from "@/src/common/lib/eden-base-query";

type GetTenantsResponse = App["~Routes"]["tenants"]["get"]["response"]["200"];
type GetTenantResponse = App["~Routes"]["tenants"][":id"]["get"]["response"]["200"];
type CreateTenantResponse = App["~Routes"]["tenants"]["post"]["response"]["200"];
type UpdateTenantResponse = App["~Routes"]["tenants"][":id"]["patch"]["response"]["200"];

export const tenantApi = createApi({
	reducerPath: "tenantApi",
	baseQuery: edenBaseQuery(),
	tagTypes: ["Tenant"],
	endpoints: (builder) => ({
		// biome-ignore lint/suspicious/noConfusingVoidType: RTK Query uses `| void` so the hook accepts no argument
		getTenants: builder.query<GetTenantsResponse, { search?: string } | void>({
			query:
				(params): EdenCall<GetTenantsResponse> =>
				(client) =>
					client.tenants.get({ query: params ?? {} }),
			providesTags: ["Tenant"],
		}),
		getTenant: builder.query<GetTenantResponse, string>({
			query:
				(id): EdenCall<GetTenantResponse> =>
				(client) =>
					client.tenants({ id }).get(),
			providesTags: (_result, _error, id) => [{ type: "Tenant", id }],
		}),
		createTenant: builder.mutation<CreateTenantResponse, CreateTenantPayload>({
			query:
				(payload): EdenCall<CreateTenantResponse> =>
				(client) =>
					client.tenants.post(payload),
			invalidatesTags: ["Tenant"],
		}),
		updateTenant: builder.mutation<UpdateTenantResponse, { id: string; payload: UpdateTenantPayload }>({
			query:
				({ id, payload }): EdenCall<UpdateTenantResponse> =>
				(client) =>
					client.tenants({ id }).patch(payload),
			invalidatesTags: (_result, _error, { id }) => [{ type: "Tenant", id }, "Tenant"],
		}),
	}),
});

export const { useGetTenantsQuery, useGetTenantQuery, useCreateTenantMutation, useUpdateTenantMutation } = tenantApi;
